import { useCallback, useLayoutEffect } from 'react'

const DEFAULT_GAP = 8
const VIEWPORT_PADDING = 8
const COMFORTABLE_MENU_WIDTH = 450
const DEFAULT_MENU_WIDTH = 414

const hasHorizontalPlacementHint = (placement) => {
  if (!placement) return false

  const normalized = placement.replace(/\s+/g, ' ').trim()
  return normalized.includes('start')
    || normalized.includes('end')
    || normalized.endsWith('left')
    || normalized.endsWith('right')
}

const parseHorizontalPlacement = (placement) => {
  const normalized = placement.replace(/\s+/g, ' ').trim()
  if (normalized.includes('end') || normalized.endsWith('right')) {
    return 'bottom-end'
  }

  return 'bottom-start'
}

export const getEstimatedMenuWidth = (menuEl, contentMinWidth) => {
  if (menuEl?.offsetWidth > 0) return menuEl.offsetWidth
  if (contentMinWidth === 'comfortable') return COMFORTABLE_MENU_WIDTH
  if (contentMinWidth === 'content') return 0
  return DEFAULT_MENU_WIDTH
}

export const resolvePortaledMenuPlacement = ({
  dataPlacement,
  contextPlacement = 'bottom start',
  pullLeft = false,
  anchorRect,
  menuWidth = 0,
  bounds,
}) => {
  if (pullLeft) return 'bottom-end'

  const placementSource = hasHorizontalPlacementHint(dataPlacement)
    ? dataPlacement
    : contextPlacement
  const horizontal = parseHorizontalPlacement(placementSource)

  if (horizontal === 'bottom-end') return 'bottom-end'

  if (menuWidth > 0 && anchorRect && bounds
    && anchorRect.left + menuWidth > bounds.right) {
    return 'bottom-end'
  }

  return 'bottom-start'
}

const SCROLL_OVERFLOW_VALUES = new Set(['auto', 'scroll', 'overlay'])

const getOverflowScrollParent = (anchorEl) => {
  if (!anchorEl) return null

  let node = anchorEl.parentElement
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node)
    if (SCROLL_OVERFLOW_VALUES.has(overflowY)) {
      return node
    }
    node = node.parentElement
  }

  return null
}

const getViewportBounds = () => ({
  top: VIEWPORT_PADDING,
  bottom: window.innerHeight - VIEWPORT_PADDING,
  left: VIEWPORT_PADDING,
  right: window.innerWidth - VIEWPORT_PADDING,
})

export const getPortaledMenuCollisionBounds = (anchorEl) => {
  const viewportBounds = getViewportBounds()

  const scrollParent = getOverflowScrollParent(anchorEl)
  if (!scrollParent) return viewportBounds

  const scrollRect = scrollParent.getBoundingClientRect()

  return {
    top: Math.max(viewportBounds.top, scrollRect.top),
    bottom: Math.min(viewportBounds.bottom, scrollRect.bottom),
    left: Math.max(viewportBounds.left, scrollRect.left),
    right: Math.min(viewportBounds.right, scrollRect.right),
  }
}

const shouldFlipMenuAbove = (anchorRect, menuHeight, bounds, gap = DEFAULT_GAP) => {
  if (!menuHeight) return false

  const belowTop = anchorRect.bottom + gap
  const fitsBelow = belowTop + menuHeight <= bounds.bottom

  if (fitsBelow) return false

  const aboveTop = anchorRect.top - menuHeight - gap
  return aboveTop >= bounds.top
}

const clampMenuTop = (top, menuHeight, bounds) => {
  if (!menuHeight) return top

  return Math.max(bounds.top, Math.min(top, bounds.bottom - menuHeight))
}

export const computePortaledMenuPosition = ({
  anchorRect,
  menuWidth,
  menuHeight,
  bounds,
  placement = 'bottom-end',
  gap = DEFAULT_GAP,
}) => {
  let top = anchorRect.bottom + gap
  let left = placement === 'bottom-start'
    ? anchorRect.left
    : anchorRect.right - menuWidth

  left = Math.max(bounds.left, Math.min(left, bounds.right - menuWidth))

  if (shouldFlipMenuAbove(anchorRect, menuHeight, bounds, gap)) {
    top = anchorRect.top - menuHeight - gap
  }

  top = clampMenuTop(top, menuHeight, bounds)

  return { top, left }
}

/**
 * Keeps a portaled dropdown menu anchored to its trigger while escaping shell overflow/z-index stacking.
 */
export const usePortaledMenuPosition = (
  isOpen,
  anchorRef,
  menuRef,
  { placement = 'bottom-end', defaultMenuWidth = 414, useViewportBounds = false } = {}
) => {
  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return

    const anchorRect = anchorRef.current.getBoundingClientRect()
    const menuWidth = menuRef.current?.offsetWidth || defaultMenuWidth
    const menuHeight = menuRef.current?.offsetHeight || 0
    const bounds = useViewportBounds
      ? getViewportBounds()
      : getPortaledMenuCollisionBounds(anchorRef.current)
    const { top, left } = computePortaledMenuPosition({
      anchorRect,
      menuWidth,
      menuHeight,
      bounds,
      placement,
    })

    if (menuRef.current) {
      menuRef.current.style.left = `${left}px`
      menuRef.current.style.top = `${top}px`
    }
  }, [anchorRef, menuRef, placement, defaultMenuWidth, useViewportBounds])

  useLayoutEffect(() => {
    if (!isOpen) return undefined

    updatePosition()
    const raf = requestAnimationFrame(updatePosition)

    let resizeObserver
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updatePosition)
      if (menuRef.current) {
        resizeObserver.observe(menuRef.current)
      }
      if (anchorRef.current) {
        resizeObserver.observe(anchorRef.current)
      }
    }

    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      cancelAnimationFrame(raf)
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [anchorRef, isOpen, menuRef, updatePosition])
}
